import * as THREE from 'three';
import { Entity } from '../../entities';
import {
  animateShipVisuals,
  buildShipSkin,
  enhanceEngineFlames
} from '../ships';
import type { EngineMount, ShipSkin } from '../ships';
import { getFaction } from '../factions';
import type { FactionId } from '../factions';
import { SHIP_BUILDERS } from '../ships/variants';

export type NpcRole = 'guard' | 'miner' | 'pirate';

export interface NpcShipOptions {
  readonly factionId: FactionId;
  readonly primaryColor: number;
  readonly role: NpcRole;
  readonly hullMax: number;
  readonly position: THREE.Vector3;
}

export class NpcShip extends Entity {
  readonly role: NpcRole;
  readonly factionId: FactionId;
  readonly primaryColor: number;
  readonly skin: ShipSkin;
  readonly engines: readonly EngineMount[];
  readonly velocity = new THREE.Vector3();
  readonly collisionRadius: number;
  readonly collisionStatic = false;
  readonly collisionRestitution = 0.2;
  readonly hullMax: number;
  hull: number;

  private readonly strobes: THREE.Mesh[];
  private time = 0;
  private thrust = 0;
  private hitFlash = 0;

  constructor(options: NpcShipOptions) {
    super();
    this.role = options.role;
    this.factionId = options.factionId;
    this.primaryColor = options.primaryColor;
    this.hullMax = options.hullMax;
    this.hull = options.hullMax;
    this.object3D.name = `NpcShip:${options.role}`;
    this.object3D.position.copy(options.position);

    const faction = getFaction(options.factionId);
    this.skin = buildShipSkin(faction, options.primaryColor);
    const builder = SHIP_BUILDERS[faction.shipVariant];
    const result = builder(this.object3D, this.skin);
    this.engines = result.engines;
    this.strobes = result.strobes;
    this.collisionRadius = result.collisionRadius;
    enhanceEngineFlames(this.engines, this.skin.plasma, this.skin.glow);
  }

  setThrust(level: number): void {
    this.thrust = THREE.MathUtils.clamp(level, 0, 1);
  }

  getCollisionCenter(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.object3D.position);
  }

  /** Returns true if the NPC is destroyed. */
  takeDamage(amount: number): boolean {
    this.hull = Math.max(0, this.hull - amount);
    this.hitFlash = 0.16;
    return this.hull <= 0;
  }

  isHitFlashing(): boolean {
    return this.hitFlash > 0;
  }

  override update(dt: number): void {
    this.time += dt;
    if (this.hitFlash > 0) this.hitFlash = Math.max(0, this.hitFlash - dt);
    // Engine pulse / strobe cycle drives shader uniforms — skip when the
    // ship is hidden by NpcDirector's frustum cull. Visibility is set by
    // the system phase before this entity update runs, so reading it here
    // is one frame fresh; visuals re-sync the moment the ship reappears.
    if (this.object3D.visible) {
      animateShipVisuals(this.time, this.thrust, this.engines, this.strobes);
    }
    this.object3D.position.addScaledVector(this.velocity, dt);
  }
}
